import React from 'react';
import { z } from 'zod';
import { Settings } from '@mui/icons-material';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import api from 'lib/trpcClient';

import TabsPanel from 'further-ui/components/TabsPanel';
import { Row } from 'further-ui/components/Layout';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { useDisclosure } from 'further-ui/hooks';
import Page from 'components/new/Page';
import ErrorBoundary from 'components/ErrorBoundary';
import PageLoader from 'components/PageLoader';
import General from './General';
import PortalSettings from './Portal';
import BankAccounts from './BankAccounts';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { lpFirm as rules } from 'further-ui/validations';
import { FormValues } from './types';
import { useTheme } from '@mui/material/styles';
import useFirmSettingsFormState from './useFirmSettingsFormState';

const validationSchema = rules.Update.extend({
  lightMainLogo: z
    .object({
      previewUri: z.string(),
      objectKey: z.string(),
    })
    .optional(),
  darkMainLogo: z
    .object({
      previewUri: z.string(),
      objectKey: z.string(),
    })
    .optional(),
  reportCoverLogo: z
    .object({
      previewUri: z.string(),
      objectKey: z.string(),
    })
    .optional(),
  squareLogo: z
    .object({
      previewUri: z.string(),
      objectKey: z.string(),
    })
    .optional(),
});

const FirmSettings: React.FC = () => {
  const mutationOpts = useHandleTrpcMutation();
  const [lpFirm] = api.lpFirm.getCurrentFirm.useSuspenseQuery();
  const updateLpFirm = api.lpFirm.update.useMutation(
    mutationOpts({
      successMessage: 'Firm settings updated successfully.',
      onSuccess: () => {
        linkedPortalFieldsDisclosure.onClose();
        // Reset dirty state without changing form values
        reset(getValues());
      },
      refetchType: 'none',
    }),
  );
  const linkedPortalFieldsDisclosure = useDisclosure();
  const theme = useTheme();
  const tabErrorDotColor = theme.palette.error.main;

  const sharedFieldDotColor = theme.palette.warning.main; // NB: if changing this color, change text below to match
  const sharedFieldWarningText =
    'Items with an orange dot below apply to both your EIS and LP portal.';

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { isDirty, errors, dirtyFields },
  } = useForm<FormValues>({
    shouldFocusError: true,
    resolver: zodResolver(validationSchema),
    values: {
      id: lpFirm._id,
      firmName: lpFirm?.firmName ?? '',
      keyContactEmails: lpFirm?.keyContactEmails ?? [],
      systemDateFormat: lpFirm?.systemDateFormat ?? 'MMM DD, YYYY',
      timezone: lpFirm?.timezone ?? 'Europe/London',
      subscriptionFlowEmailRecipients:
        lpFirm?.subscriptionFlowEmailRecipients ?? [],
      verifiedReplyToEmailAddress: lpFirm?.verifiedReplyToEmailAddress ?? '',
      contactEmail: lpFirm?.contactEmail ?? '',
      contactPhoneNumber: lpFirm?.contactPhoneNumber ?? '',
      contactHours: lpFirm?.contactHours ?? '',
      portalSubdomain: lpFirm?.portalSubdomain ?? '',
      lightMainLogo: lpFirm?.lightMainLogo,
      darkMainLogo: lpFirm?.darkMainLogo,
      panelColor: lpFirm?.panelColor ?? '',
      secondaryColor: lpFirm?.secondaryColor ?? '',
      highlightColor: lpFirm?.highlightColor ?? '',
      reportCoverColor: lpFirm?.reportCoverColor ?? '',
      reportCoverLogo: lpFirm?.reportCoverLogo,
      squareLogo: lpFirm?.squareLogo,
      portalFooterText: lpFirm?.portalFooterText ?? '',
      emailFooterText: lpFirm?.emailFooterText ?? '',
    },
  });

  const {
    hasGeneralTabErrors,
    hasPortalTabGeneralSubTabErrors,
    hasPortalTabBrandingSubTabErrors,
    hasPortalTabErrors,
    hasLinkedPortalFieldChanges,
  } = useFirmSettingsFormState({
    errors,
    dirtyFields,
    hasLinkedPortal: lpFirm.hasLinkedPortal,
  });

  const onSubmit = (data: FormValues) => {
    if (hasLinkedPortalFieldChanges && !linkedPortalFieldsDisclosure.isOpen) {
      linkedPortalFieldsDisclosure.onOpen();
    } else {
      updateLpFirm.mutate({
        ...data,
        lightMainLogo: data.lightMainLogo?.objectKey,
        darkMainLogo: data.darkMainLogo?.objectKey,
        reportCoverLogo: data.reportCoverLogo?.objectKey ?? null,
        squareLogo: data.squareLogo?.objectKey,
      });
    }
  };

  const firmSettingsTabs = [
    {
      key: 'general',
      label: 'General',
      content: (
        <General
          control={control}
          hasLinkedPortal={lpFirm.hasLinkedPortal}
          sharedFieldWarningText={sharedFieldWarningText}
          sharedFieldDotColor={sharedFieldDotColor}
        />
      ),
      urlParam: 'general',
      dotColor: hasGeneralTabErrors ? tabErrorDotColor : undefined,
    },
    {
      key: 'portal',
      label: 'Portal',
      content: (
        <PortalSettings
          control={control}
          hasLinkedPortal={lpFirm.hasLinkedPortal}
          hasGeneralSubTabErrors={hasPortalTabGeneralSubTabErrors}
          hasBrandingSubTabErrors={hasPortalTabBrandingSubTabErrors}
          sharedFieldWarningText={sharedFieldWarningText}
          sharedFieldDotColor={sharedFieldDotColor}
        />
      ),
      urlParam: 'portal',
      dotColor: hasPortalTabErrors ? tabErrorDotColor : undefined,
    },
    {
      key: 'bank-accounts',
      label: 'Bank accounts',
      content: <BankAccounts />,
      urlParam: 'bank-accounts',
    },
  ];

  return (
    <Page
      tabTitle="Firm Settings"
      heading="Firm Settings"
      headingIcon={Settings}
      spacing="sm"
      actionBar={
        isDirty ? (
          <Row justify="flex-end" centered spacing="sm">
            {hasGeneralTabErrors ? (
              <Typography variant="body2" color="error">
                Please review errors on the General tab.
              </Typography>
            ) : hasPortalTabErrors ? (
              <Typography variant="body2" color="error">
                Please review errors on the Portal tab.
              </Typography>
            ) : (
              ''
            )}
            <Button
              variant="contained"
              color="primary"
              type="submit"
              loading={updateLpFirm.isPending}
              onClick={handleSubmit(onSubmit)}
              size="large"
            >
              Save
            </Button>
          </Row>
        ) : null
      }
    >
      <ErrorBoundary loader={<PageLoader />}>
        <TabsPanel tabs={firmSettingsTabs} />
      </ErrorBoundary>
      <ConfirmDialog
        open={linkedPortalFieldsDisclosure.isOpen}
        onClose={linkedPortalFieldsDisclosure.onClose}
        maxWidth="sm"
        title="Confirm changes"
        loading={updateLpFirm.isPending}
        content="Some of the information you're about to save will apply to both your LP and EIS portal."
        actions={{
          confirm: {
            onClick: handleSubmit(onSubmit),
            label: 'Save',
          },
          cancel: {
            onClick: linkedPortalFieldsDisclosure.onClose,
            label: 'Cancel',
          },
        }}
      />
    </Page>
  );
};

export default FirmSettings;
